import { useEffect, useState } from "react";
import { Loader2, Clock, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Shift editor for each team member's start/end. Super admins see everyone;
 *  anyone else holding `inout.edit` sees and edits their own team(s) only.
 *  Late-start (and early-finish) is measured against this schedule, falling
 *  back to the team window then the global default when a field is left blank. */
export const ShiftScheduleDialog = ({ open, onOpenChange, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ default_start: "07:30", default_end: "", grace_minutes: 5 });
  const [scope, setScope] = useState(null);
  const [rows, setRows] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/inout/shifts")
      .then((r) => {
        setMeta({ default_start: r.data.default_start, default_end: r.data.default_end, grace_minutes: r.data.grace_minutes });
        setScope(r.data.scope || null);
        setRows((r.data.items || []).map((u) => ({ ...u, _start: u.shift_start || "", _end: u.shift_end || "" })));
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load shift schedules"))
      .finally(() => setLoading(false));
  }, [open]);

  const setField = (uid, key, val) =>
    setRows((rs) => rs.map((r) => (r.user_id === uid ? { ...r, [key]: val } : r)));

  const save = async (row) => {
    const start = (row._start || "").trim();
    const end = (row._end || "").trim();
    if (start && !HHMM.test(start)) return toast.error("Start must be HH:MM (24-hour), e.g. 08:30");
    if (end && !HHMM.test(end)) return toast.error("End must be HH:MM (24-hour), e.g. 17:00");
    setSavingId(row.user_id);
    try {
      await api.put(`/inout/shifts/${row.user_id}`, { shift_start: start, shift_end: end });
      setRows((rs) => rs.map((r) => (r.user_id === row.user_id
        ? { ...r, shift_start: start, shift_end: end } : r)));
      toast.success(`Saved ${row.name}'s shift`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that shift");
    } finally {
      setSavingId(null);
    }
  };

  const dirty = (r) => (r._start || "") !== (r.shift_start || "") || (r._end || "") !== (r.shift_end || "");
  const visible = rows.filter((r) => !q || (r.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 p-0" data-testid="shift-schedule-dialog">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4 text-primary" /> Shift schedules
          </DialogTitle>
          <DialogDescription className="text-xs">
            Set each person's expected start (and optional finish) in 24-hour time.
            Blank uses the team window, then the default {meta.default_start}
            {meta.default_end ? `–${meta.default_end}` : ""} · {meta.grace_minutes} min grace.
          </DialogDescription>
          {scope && (
            <p className="text-[11px] text-primary" data-testid="shift-schedule-scope">
              {scope.teams?.length
                ? <>Your team{scope.teams.length === 1 ? "" : "s"}: <span className="font-medium">{scope.teams.join(", ")}</span> — only their shifts are listed.</>
                : "You are not on a team yet, so there is nobody to schedule — ask an admin to add you to one."}
            </p>
          )}
        </DialogHeader>

        <div className="border-b border-border px-6 py-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter people…"
                 className="h-8 text-sm" data-testid="shift-schedule-filter" />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="shift-schedule-list">
              {visible.map((r) => (
                <li key={r.user_id} className="flex flex-wrap items-center gap-3 py-2"
                    data-testid={`shift-schedule-row-${r.user_id}`}>
                  <Avatar name={r.name} src={r.avatar_url} className="h-7 w-7 shrink-0" />
                  <div className="min-w-[130px] flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{r.name}</div>
                    <div className="truncate text-[11px] text-muted-foreground">{r.team || "—"}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Input value={r._start} onChange={(e) => setField(r.user_id, "_start", e.target.value)}
                           placeholder={meta.default_start} className="h-8 w-[74px] text-center font-mono text-xs"
                           data-testid={`shift-start-${r.user_id}`} />
                    <span className="text-xs text-muted-foreground">→</span>
                    <Input value={r._end} onChange={(e) => setField(r.user_id, "_end", e.target.value)}
                           placeholder={meta.default_end || "—"} className="h-8 w-[74px] text-center font-mono text-xs"
                           data-testid={`shift-end-${r.user_id}`} />
                  </div>
                  <Button size="sm" variant={dirty(r) ? "default" : "outline"} disabled={!dirty(r) || savingId === r.user_id}
                          onClick={() => save(r)} className="h-8" data-testid={`shift-save-${r.user_id}`}>
                    {savingId === r.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  </Button>
                </li>
              ))}
              {visible.length === 0 && (
                <li className="py-8 text-center text-sm text-muted-foreground" data-testid="shift-schedule-empty">
                  {rows.length === 0 ? "No one to schedule." : `No people match "${q}".`}
                </li>
              )}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftScheduleDialog;
